import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  arrayUnion,
  Timestamp,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Bid, ProxyBid, BidValidationResult } from '../types/bid.types';
import { Auction, AuctionLot, BidIncrementBand } from '../types/auction.types';
// Soft-close timing helpers are no longer used here — soft-close moved SERVER-SIDE
// (executeProxyBids onCreate trigger). See src/utils/lotTiming.ts.

// ---------------------------------------------------------------------------
// Bid-increment schedule — DATA-DRIVEN. The canonical schedule lives on the
// auction doc (auction.bidIncrements). When absent, both the client and the
// engine fall back to DEFAULT_SCHEDULE below. The engine (functions/src/index.ts)
// ports incrementFor/nextMinBidFor + DEFAULT_SCHEDULE VERBATIM — keep the two
// copies byte-identical (functions/ is a separate build and can't import this).
//
// Schmidt Auctions / HiBid published table, CENTS, ascending, INCLUSIVE upTo,
// last band open (upTo:null):
//   ≤ $9.00 → $1.00 | ≤ $18.00 → $2.00 | ≤ $47.50 → $2.50 | ≤ $95.00 → $5.00
//   ≤ $250.00 → $10.00 | ≤ $475.00 → $25.00 | ≤ $950.00 → $50.00
//   ≤ $2,500.00 → $100.00 | > $2,500.00 → $250.00
// ---------------------------------------------------------------------------
export const DEFAULT_SCHEDULE: BidIncrementBand[] = [
  { upTo: 900, increment: 100 },
  { upTo: 1800, increment: 200 },
  { upTo: 4750, increment: 250 },
  { upTo: 9500, increment: 500 },
  { upTo: 25000, increment: 1000 },
  { upTo: 47500, increment: 2500 },
  { upTo: 95000, increment: 5000 },
  { upTo: 250000, increment: 10000 },
  { upTo: null, increment: 25000 },
];

/**
 * The increment for a given current bid, read from a schedule. Inclusive upTo;
 * the open final band (upTo:null) catches everything above the last threshold.
 */
export const incrementFor = (
  current: number,
  schedule: BidIncrementBand[] = DEFAULT_SCHEDULE
): number => {
  for (const b of schedule) {
    if (b.upTo == null || current <= b.upTo) return b.increment;
  }
  return schedule[schedule.length - 1].increment;
};

/**
 * The next minimum bid for a given current bid, read from a schedule.
 */
export const nextMinBidFor = (
  current: number,
  schedule: BidIncrementBand[] = DEFAULT_SCHEDULE
): number => current + incrementFor(current, schedule);

/**
 * Calculate next minimum bid. Back-compat name; now schedule-aware. Callers that
 * have the auction should pass auction.bidIncrements; otherwise DEFAULT_SCHEDULE.
 */
export const calculateNextMinBid = (
  currentBid: number,
  schedule: BidIncrementBand[] = DEFAULT_SCHEDULE
): number => nextMinBidFor(currentBid, schedule);

/**
 * Get the bid increment for a given price. Back-compat name; now schedule-aware.
 */
export const getBidIncrement = (
  currentBid: number,
  schedule: BidIncrementBand[] = DEFAULT_SCHEDULE
): number => incrementFor(currentBid, schedule);

/**
 * SHARED opening-bid rule — the ONE place that answers "what is the minimum acceptable bid on
 * this lot right now?".
 *
 * Both validateBid (direct bids) and placeProxyBid (proxy opening bids) MUST use this. They
 * previously computed it independently and drifted: placeProxyBid read the lot cache
 * (`lot.currentBid || lot.startingBid`) and always added an increment, so a proxy on an EMPTY
 * $3 lot opened at $4 instead of $3. This is the third bug of that class (bid increments, the
 * winning-bid source, now the opening bid) — one helper prevents a fourth.
 *
 * Authoritative source is the `bids` collection, NOT the auctions-doc lot cache: ImportCSV
 * seeds `currentBid = startingBid` on lots with zero bids, so the cache literally cannot
 * express "no bids yet".
 */
export interface OpeningBidResult {
  /** The current winning bid doc (authoritative), or null when the lot has no bids. */
  winner: any | null;
  hasNoBids: boolean;
  /** Current price: the winning bid amount, or the starting bid on an empty lot. */
  currentBid: number;
  /** Minimum acceptable bid NOW: startingBid on an empty lot, else current + increment. */
  openingBid: number;
}

export const computeOpeningBid = async (
  auctionId: string,
  lotId: string,
  lot: any,
  increments?: BidIncrementBand[],
): Promise<OpeningBidResult> => {
  const winningSnap = await getDocs(query(
    collection(db, 'bids'),
    where('auctionId', '==', auctionId),
    where('lotId', '==', lotId),
    where('isWinning', '==', true),
  ));
  // Defensive: if more than one is (transiently) flagged, take the highest amount.
  let winner: any = null;
  winningSnap.forEach((d) => {
    const b = d.data() as any;
    if (!winner || (b.amount ?? 0) > (winner.amount ?? 0)) winner = b;
  });

  const hasNoBids = !winner;
  const currentBid = winner ? (winner.amount ?? 0) : (lot.startingBid || 0);
  // EMPTY LOT: bidding opens AT the starting price (published term #9 — a proxy spends only
  // what's needed to lead, and with no competition that's the opening price).
  // CONTESTED LOT: unchanged — one increment above the standing bid.
  const openingBid = hasNoBids
    ? (lot.startingBid ?? 0)
    : calculateNextMinBid(currentBid, increments);

  return { winner, hasNoBids, currentBid, openingBid };
};

/**
 * Validate a bid amount
 */
export const validateBid = async (
  auctionId: string,
  lotId: string,
  bidAmount: number,
  bidderId: string
): Promise<BidValidationResult> => {
  try {
    // Get specific auction document
    const auctionRef = doc(db, 'auctions', auctionId);
    const auctionSnap = await getDoc(auctionRef);
    
    if (!auctionSnap.exists()) {
      return { valid: false, error: 'Auction not found', code: 'not_found' };
    }
    
    const auction = auctionSnap.data();
    const lot = auction.lots?.find((l: any) => l.id === lotId);

    if (!lot) {
      return { valid: false, error: 'Lot not found', code: 'not_found' };
    }

    // NO END-TIME CHECK HERE, deliberately (Oct 2026). This used to compare the lot's end with
    // the bidder's OWN computer clock and return "This lot has closed" before any write — so a
    // machine running 30s fast refused every lot inside its last 30s (the soft-close window),
    // with nothing recorded anywhere. Whether a lot is open is decided by the SERVER only:
    // executeProxyBids voids a late bid as 'lot_closed', and firestore.rules refuses a write
    // after the sale's biddingHardStop. The browser's clock never decides whether a bid is tried.

    // SHARED opening-bid rule (see computeOpeningBid): authoritative `bids` collection, with
    // the empty-lot branch. Proxy opening bids use the exact same helper so they can't drift.
    const { winner, hasNoBids, currentBid, openingBid: nextMinBid } =
      await computeOpeningBid(auctionId, lotId, lot, auction.bidIncrements);
    const currentBidderId: string | undefined = winner?.bidderId;

    console.log('🔍 Bid validation (authoritative bids source):', {
      lotNumber: lot.lotNumber,
      hasNoBids,
      startingBid: lot.startingBid,
      currentBid,
      currentBidderId,
      bidAmount,
      nextMinBid,
    });

    // NO MINIMUM CHECK HERE either (Oct 2026): the engine refuses a low bid ('below_minimum',
    // with the minimum it applied), and the bidder is told that minimum. The UI shows the next
    // valid amount as guidance; a refused low bid costs one round trip and a clear message.

    // ALREADY HIGH BIDDER — KEPT, and load-bearing. The engine does NOT refuse a bid from the
    // account that already leads: a manual bid at or above the minimum is GRANTED and raises that
    // bidder's own price (executeProxyBids, Phase 1 — no self-bid check). Until the server refuses
    // it, this guard is what stops a carried-forward holder raising their own price on a Bid Off.
    // It also stops placeProxyBid's opening bid from self-raising a leader who only lifts a max.
    if (currentBidderId === bidderId) {
      return {
        valid: false,
        code: 'already_high',
        error: 'You are already the high bidder',
        nextMinBid,
        lotNumber: lot.lotNumber,
        lotTitle: lot.title,
      };
    }

    return { valid: true, nextMinBid, lotNumber: lot.lotNumber, lotTitle: lot.title };

  } catch (error) {
    console.error('Bid validation error:', error);
    return { valid: false, error: 'Validation failed', code: 'validation_error' };
  }
};

/**
 * Handle soft close extension when bid is placed
 */
export const handleSoftCloseExtension = async (
  auctionId: string,
  lotId: string,
  auction: Auction,
  lot: AuctionLot
): Promise<void> => {
  // DEAD / DEPRECATED: soft-close extension is now handled SERVER-SIDE by the
  // executeProxyBids onCreate trigger (for BOTH manual and proxy auto-bids). This
  // client function is no longer called (removed from placeBid) and is a no-op so
  // it can never double-extend. Kept (not fully deleted) to avoid a risky large
  // edit on this file; delete in a follow-up cleanup commit.
  void auctionId; void lotId; void auction; void lot;
};

// markBidsAsOutbid was REMOVED — outbid-marking is now server-side (the
// executeProxyBids onCreate trigger marks prior winning bids outbid via the
// Admin SDK). Clients no longer write to other users' bid docs.

// ---------------------------------------------------------------------------
// BIDDER GATE — a verified card on file. Judged on what the account HOLDS, nothing else.
//
// Standing rules (Schmidt Auctions is a licensed PA auction house):
//   1. Every bidder is registered (KYC). There is NO guest path — it has been deleted.
//   2. Nobody bids without a verified card on file.
//
// "Verified card on file" = users/{uid}.authnet.customerProfileId AND .paymentProfileId, both
// present. registerForSale writes them ONLY after a successful $0 Authorize.net authorization, and
// they are what settleInvoice charges. NOT users.cardAuthorized: a legacy flag an old test button set
// with no authorization behind it (live carded bidders have it false).
//
// NOT a guest marker. guestBidder:true and a "G-" bidder number say how an account ARRIVED (the old
// quick sign-up), not what is on file now: a G- account with a verified card passes, and the fix
// for it is staff clearing the flag and issuing a normal number — never a block (Dan, 20 Oct 2026).
//
// The users doc is read FRESH here, never from the AuthContext profile: that copy is loaded once
// at sign-in and does not see a card added a minute ago, which would wrongly block a new bidder.
// Client-side check: the matching firestore.rules clause is written up for after the sale.
// ---------------------------------------------------------------------------
// ✏️ Wording is Dan's to approve.
export const BID_GATE_MESSAGES = {
  no_card:
    'We need a verified card on file before you can bid. Where registration is open, use "Register ' +
    'for this sale" at the top of the sale page — it adds your card with a $0 authorization, and ' +
    'nothing is charged unless you win. Otherwise, or if you are already registered and still see ' +
    'this, please call us at 484-366-1780.',
  unreadable: "We couldn't confirm your account just now. Please try again in a moment.",
};

// One flat shape (not a discriminated union): this app compiles with strict:false, where
// narrowing on `ok` does not happen, so reason/message are optional and set whenever ok is false.
export interface BidEligibility {
  ok: boolean;
  reason?: 'no_card' | 'unreadable';
  message?: string;
}

// PASS CACHE — takes the users read out of the hot path of a sale close.
//
// Only PASSES are cached. A blocked result (no_card, unreadable) is never stored, so a
// blocked user is re-read on EVERY attempt: the moment a verified card lands on their account, their very next bid goes through with no wait. That is the property the "read FRESH"
// note above protects — it only ever mattered for people who are currently failing.
// A pass is trusted for 15 minutes: long enough to cover a close, short enough that a mid-sale
// revocation (a card removed) takes effect quickly. Per browser tab, in memory only;
// cleared on every sign-in/sign-out change (AuthContext) so a shared browser can't hand one
// person's pass to the next.
const BID_GATE_PASS_TTL_MS = 15 * 60 * 1000;
const BID_GATE_RETRY_DELAY_MS = 250;
const bidGatePassCache = new Map<string, number>(); // uid -> time of the last PASS (ms)

/** Drop one uid's cached pass, or (no argument) every cached pass. */
export const clearBidEligibilityCache = (uid?: string): void => {
  if (uid) bidGatePassCache.delete(uid);
  else bidGatePassCache.clear();
};

export const checkBidEligibility = async (uid: string): Promise<BidEligibility> => {
  const passedAt = bidGatePassCache.get(uid);
  if (passedAt !== undefined && Date.now() - passedAt < BID_GATE_PASS_TTL_MS) {
    return { ok: true };
  }
  bidGatePassCache.delete(uid); // expired (or absent): re-decide from a fresh read below

  let u: any = null;
  try {
    let snap;
    try {
      snap = await getDoc(doc(db, 'users', uid));
    } catch (first) {
      // ONE retry after a short pause, never more — a sale close is no place for a retry storm.
      console.warn('Bid gate: users read failed, retrying once', first);
      await new Promise((r) => setTimeout(r, BID_GATE_RETRY_DELAY_MS));
      snap = await getDoc(doc(db, 'users', uid));
    }
    u = snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error('Bid gate: could not read users doc (after one retry)', e);
    return { ok: false, reason: 'unreadable', message: BID_GATE_MESSAGES.unreadable };
  }
  const a = u?.authnet || {};
  if (!a.customerProfileId || !a.paymentProfileId) {
    return { ok: false, reason: 'no_card', message: BID_GATE_MESSAGES.no_card };
  }
  bidGatePassCache.set(uid, Date.now()); // the ONLY place anything is cached: a pass
  return { ok: true };
};

/**
 * Why placeBid did not write a bid (or could not confirm the write). The caller turns this into
 * one of three visible outcomes (services/bidOutcome.ts): accepted / refused because closed /
 * could not reach the server. It is NEVER a time-based refusal — no browser clock decides.
 *   not_registered     no saleRegistrations doc for this bidder on this sale
 *   no_card            the card-on-file gate (checkBidEligibility)
 *   already_high       the bidder already leads this lot (see validateBid)
 *   not_found          sale or lot missing
 *   permission_denied  firestore.rules refused the write — the reason is NOT in the error; the
 *                      caller must work it out from live data or say it does not know
 *   unreachable        a network failure, or no reply from the server in time; maybeSent = the
 *                      SDK may still deliver the write later (it queues writes offline)
 *   unexpected         anything else (validation error, unknown error)
 */
export type PlaceBidFailure =
  | 'not_registered' | 'no_card' | 'already_high' | 'not_found'
  | 'permission_denied' | 'unreachable' | 'unexpected';

/** How long to wait for the bid write to be acknowledged before telling the bidder we could not
 *  confirm it reached the server. The write is not cancelled: the SDK keeps it queued. */
export const BID_WRITE_TIMEOUT_MS = 10000;

const NETWORK_CODES = new Set(['unavailable', 'deadline-exceeded', 'cancelled', 'resource-exhausted']);

/**
 * Place a manual bid
 */
export const placeBid = async (
  auctionId: string,
  lotId: string,
  bidAmount: number,
  bidderId: string,
  bidderName: string,
  bidderNumber?: string
): Promise<{ success: boolean; bid?: Bid; error?: string; failure?: PlaceBidFailure; maybeSent?: boolean; bidId?: string }> => {
  try {
    console.log('🔨 Placing bid:', { auctionId, lotId, bidAmount, bidderId });

    // Phase 1a per-sale registration gate: the bidder must have explicitly
    // registered for THIS auction before any bid is accepted. Nobody is
    // auto-registered. (Authoritative check — the UI mirrors this, but bids
    // are rejected here regardless of client state.)
    const regSnap = await getDoc(doc(db, 'saleRegistrations', `${auctionId}_${bidderId}`));
    if (!regSnap.exists()) {
      console.error('❌ Bid blocked — bidder not registered for this sale:', bidderId);
      return { success: false, error: 'Please register for this sale before bidding.', failure: 'not_registered' };
    }

    // Identity + card-on-file gate (see BIDDER GATE above). Before validation, before any write.
    const gate = await checkBidEligibility(bidderId);
    if (!gate.ok) {
      console.error(`❌ Bid blocked — ${gate.reason}:`, bidderId);
      return { success: false, error: gate.message, failure: gate.reason === 'no_card' ? 'no_card' : 'unreachable' };
    }

    // Validate the bid (authoritative source: the bids collection). validateBid also
    // returns the lot display metadata so we can stamp the bid doc WITHOUT a second
    // auction read and WITHOUT any lot-cache write.
    const validation = await validateBid(auctionId, lotId, bidAmount, bidderId);
    if (!validation.valid) {
      console.error('❌ Bid validation failed:', validation.error);
      return {
        success: false,
        error: validation.error,
        failure: validation.code === 'already_high' ? 'already_high' : validation.code === 'not_found' ? 'not_found' : 'unexpected',
      };
    }

    const now = new Date();

    // Create the bid object. lotNumber/lotTitle come from validateBid's resolved lot.
    // The client PROPOSES the bid ('pending', not winning). The executeProxyBids trigger
    // validates it against the minimum and GRANTS it (status 'winning') in the same
    // transaction that demotes the incumbent — or refuses it. Anything that goes wrong
    // server-side leaves it un-promoted (fail-closed). The validateBid check above stays as
    // the fast local check that gives an immediate error; it is no longer the authority.
    const bid: Omit<Bid, 'id'> = {
      auctionId,
      lotId,
      lotNumber: validation.lotNumber ?? '',
      lotTitle: validation.lotTitle ?? '',
      bidderId,
      bidderName,
      bidderNumber: bidderNumber ?? bidderId.slice(0, 6),
      bidType: 'manual',
      amount: bidAmount,
      status: 'pending',
      isWinning: false,
      placedAt: now,
    };

    console.log('📝 Creating bid document:', bid);

    // THE ONLY WRITE: add the bid to the authoritative `bids` collection. The auctions
    // lot cache (currentBid/currentBidder/currentBidderName/bidCount/lastBidAt) is now
    // written SERVER-SIDE, transactionally, by the executeProxyBids Phase 1 trigger. The
    // client no longer touches lots[] — that non-transactional read-modify-write was the
    // lost-update source. Outbid-marking + soft-close are already server-side too, so
    // placeBid does exactly one thing: create the bid doc.
    // placedAt is the SERVER's clock (serverTimestamp), never the bidder's machine — ordering
    // and lateness must not be decided by the browser. firestore.rules will require it.
    // The id is chosen HERE, before the write, so the bid can be followed even if the write is not
    // acknowledged in time: the caller keeps watching bids/{id} and the outcome updates itself the
    // moment the server decides (services/bidOutcome.ts). The write is acknowledged by the server
    // before setDoc resolves; offline the SDK queues it and setDoc does not resolve — so race it
    // against a timeout rather than leave "Placing…" forever. On timeout the write is NOT cancelled.
    const bidRef = doc(collection(db, 'bids'));
    let writeTimer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<'timeout'>((resolve) => {
      writeTimer = setTimeout(() => resolve('timeout'), BID_WRITE_TIMEOUT_MS);
    });
    const written = await Promise.race([
      setDoc(bidRef, { ...bid, placedAt: serverTimestamp() }).then(() => 'ok' as const),
      timedOut,
    ]);
    clearTimeout(writeTimer);
    if (written === 'timeout') {
      console.error('❌ Bid write not acknowledged within', BID_WRITE_TIMEOUT_MS, 'ms');
      return { success: false, error: 'No reply from the server.', failure: 'unreachable', maybeSent: true, bidId: bidRef.id };
    }

    console.log('✅ Bid document created:', bidRef.id);

    return {
      success: true,
      bid: { ...bid, id: bidRef.id } as Bid,
    };

  } catch (error) {
    console.error('❌ Place bid error:', error);
    const code = (error as any)?.code as string | undefined;
    const failure: PlaceBidFailure = code === 'permission-denied' ? 'permission_denied'
      : code && NETWORK_CODES.has(code) ? 'unreachable'
      : (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'unreachable'
      : 'unexpected';
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to place bid',
      failure,
      maybeSent: false,
    };
  }
};

/**
 * How a just-placed bid came out, once the executeProxyBids trigger has evaluated it.
 */
export type BidResolution =
  | { kind: 'winning' }
  | { kind: 'outbid' }
  | { kind: 'below_minimum'; minAcceptable?: number }
  | { kind: 'lot_closed' }
  | { kind: 'rejected'; voidReason?: string }
  | { kind: 'timeout' };

export const BID_RESOLUTION_TIMEOUT_MS = 12000;

/**
 * Wait for the server to grant or refuse a bid this client just wrote as 'pending'.
 * Resolves on the first non-pending status, or { kind: 'timeout' } after timeoutMs.
 * Never retries and never writes — a timeout means "still being confirmed", not "failed".
 */
export const awaitBidResolution = (
  bidId: string,
  timeoutMs: number = BID_RESOLUTION_TIMEOUT_MS,
): Promise<BidResolution> =>
  new Promise((resolve) => {
    let done = false;
    let unsub: (() => void) | null = null;
    const finish = (r: BidResolution) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (unsub) unsub();
      resolve(r);
    };
    const timer = setTimeout(() => finish({ kind: 'timeout' }), timeoutMs);
    unsub = onSnapshot(
      doc(db, 'bids', bidId),
      (snap) => {
        const d = snap.data() as any;
        if (!d) return;
        if (d.status === 'winning') return finish({ kind: 'winning' });
        if (d.status === 'outbid') return finish({ kind: 'outbid' });
        if (d.status === 'rejected') {
          if (d.voidReason === 'below_minimum') {
            return finish({ kind: 'below_minimum', minAcceptable: d.minAcceptableAtEvaluation });
          }
          if (d.voidReason === 'lot_closed') return finish({ kind: 'lot_closed' });
          return finish({ kind: 'rejected', voidReason: d.voidReason });
        }
        // 'pending' (or anything unrecognised): keep waiting until the timeout.
      },
      // A listener error is not a verdict on the bid — report it as still unconfirmed.
      () => finish({ kind: 'timeout' }),
    );
    if (done && unsub) unsub();
  });

/**
 * The bidder-facing message for a resolution that is NOT a win (null for 'winning', which
 * keeps each caller's existing success path).
 */
export const bidResolutionMessage = (r: BidResolution): string | null => {
  switch (r.kind) {
    case 'winning':
      return null;
    case 'outbid':
      return 'Someone bid higher while yours was being placed.';
    case 'below_minimum':
      return typeof r.minAcceptable === 'number'
        ? `The minimum is now $${(r.minAcceptable / 100).toFixed(2)}.`
        : 'Your bid was below the current minimum.';
    case 'lot_closed':
      return 'This lot has closed.';
    case 'rejected':
      return 'Your bid could not be accepted.';
    case 'timeout':
      return "Your bid is still being confirmed — don't bid again, refresh in a moment.";
  }
};

/**
 * Place a proxy bid (set max amount, system auto-bids)
 */
export const placeProxyBid = async (
  auctionId: string,
  lotId: string,
  maxAmount: number,
  bidderId: string,
  bidderName: string
): Promise<{ success: boolean; proxyBid?: ProxyBid; error?: string; openingBidId?: string }> => {
  try {
    console.log('🤖 Placing proxy bid:', { auctionId, lotId, maxAmount, bidderId });

    // Same gate as placeBid, and it MUST run here, before the proxyBids doc is written: the
    // server-side proxy engine bids on behalf of any active proxy, so a proxy saved by an
    // ungated user would bid for them even though their own opening bid was refused.
    const gate = await checkBidEligibility(bidderId);
    if (!gate.ok) {
      console.error(`❌ Proxy blocked — ${gate.reason}:`, bidderId);
      return { success: false, error: gate.message };
    }
    
    // Get auction and lot data
    const auctionRef = doc(db, 'auctions', auctionId);
    const auctionSnap = await getDoc(auctionRef);
    
    if (!auctionSnap.exists()) {
      return { success: false, error: 'Auction not found' };
    }
    
    const auction = auctionSnap.data();
    const lot = auction.lots?.find((l: any) => l.id === lotId);
    
    if (!lot) {
      return { success: false, error: 'Lot not found' };
    }
    
    // SHARED opening-bid rule — identical to validateBid. Previously this read the lot cache
    // and ALWAYS added an increment, so a proxy on an empty $3 lot opened at $4. The cache
    // can't express "no bids" (ImportCSV seeds currentBid = startingBid), which is why the
    // authoritative bids query in the helper is required.
    const { openingBid: nextMinBid } =
      await computeOpeningBid(auctionId, lotId, lot, auction.bidIncrements);

    // A max EQUAL to the opening bid is valid (a $3 max on an empty $3 lot). The old check
    // compared against startingBid+increment and wrongly refused it.
    if (maxAmount < nextMinBid) {
      return {
        success: false,
        error: `Proxy bid must be at least $${(nextMinBid / 100).toFixed(2)}`,
      };
    }
    
    const now = new Date();

    // DEDUPE: a bidder may only have ONE active proxy per lot. If one already exists,
    // RAISE its maxAmount in place instead of creating a second doc (the duplicate bug).
    // Equality-only query on (auctionId, lotId, bidderId, isActive) — served by
    // single-field indexes, so NO composite index is required. Constraining on
    // bidderId == the caller also satisfies the proxyBids read rule.
    const existingSnap = await getDocs(query(
      collection(db, 'proxyBids'),
      where('auctionId', '==', auctionId),
      where('lotId', '==', lotId),
      where('bidderId', '==', bidderId),
      where('isActive', '==', true),
    ));

    let proxyRef;
    let proxyBid: Omit<ProxyBid, 'id'>;
    if (!existingSnap.empty) {
      // Reuse (and raise) the existing active proxy.
      proxyRef = existingSnap.docs[0].ref;
      const prev = existingSnap.docs[0].data() as any;
      proxyBid = {
        auctionId,
        lotId,
        bidderId,
        bidderName,
        maxAmount,
        currentAmount: prev.currentAmount ?? nextMinBid,
        isActive: true,
        createdAt: prev.createdAt?.toDate?.() ?? now,
        executedBids: prev.executedBids ?? [],
      };
      await updateDoc(proxyRef, {
        maxAmount,
        isActive: true,
        updatedAt: Timestamp.fromDate(now),
      });
      console.log('🔁 Existing active proxy raised (no duplicate created):', proxyRef.id);
    } else {
      proxyBid = {
        auctionId,
        lotId,
        bidderId,
        bidderName,
        maxAmount,
        currentAmount: nextMinBid,
        isActive: true,
        createdAt: now,
        executedBids: [],
      };
      console.log('📝 Creating proxy bid document:', proxyBid);
      proxyRef = await addDoc(collection(db, 'proxyBids'), {
        ...proxyBid,
        createdAt: Timestamp.fromDate(now),
      });
      console.log('✅ Proxy bid created:', proxyRef.id);
    }

    // Execute initial bid at minimum amount. If the bidder already leads, validateBid
    // rejects this no-op (they stay winning) — the raised max still took effect above.
    const initialBid = await placeBid(
      auctionId,
      lotId,
      nextMinBid,
      bidderId,
      bidderName,
      undefined
    );

    if (initialBid.success && initialBid.bid) {
      // Record the executed bid id (arrayUnion so a raise preserves prior history).
      await updateDoc(proxyRef, {
        executedBids: arrayUnion(initialBid.bid.id),
      });
      console.log('✅ Proxy bid initial execution complete');
    }

    return {
      success: true,
      proxyBid: { ...proxyBid, id: proxyRef.id } as ProxyBid,
      // The opening bid was written 'pending' — the caller must await its resolution before
      // telling the bidder anything about the lead. Absent when no opening bid was written
      // (e.g. the bidder already leads and only raised their max).
      openingBidId: initialBid.success && initialBid.bid ? initialBid.bid.id : undefined,
    };
    
  } catch (error) {
    console.error('❌ Proxy bid error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to place proxy bid',
    };
  }
};

/**
 * Cancel/deactivate a proxy bid
 */
export const cancelProxyBid = async (
  proxyBidId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await updateDoc(doc(db, 'proxyBids', proxyBidId), {
      isActive: false,
      updatedAt: Timestamp.fromDate(new Date()),
    });
    return { success: true };
  } catch (error) {
    console.error('Cancel proxy bid error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to cancel proxy bid',
    };
  }
};
